import {
  Button,
  Dimmer,
  Divider,
  Form,
  Header,
  Icon,
  List,
  Loader,
  Modal,
  Popup,
  Segment,
  TextArea,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import StripedMessage from '@/components/Autre/StripedMessage';
import { MODES_REGLEMENTS, REGISSEUR } from '@/constants';
import { RecuContext } from '@/contexts';
import { getRecuReglements, getRecusAnterieurs, printRecu } from '@/routes';
import type { DataReglementType, RecuEncaissementType } from '@/types';
import { extractApiErrorMessage } from '@/utils/apiErrors';
import { useRegieStore, useRegisseurStore } from '@/zustand';

import { ReglementsInline } from '../Reglement/Inline';
import ModalAnnulationRecu from './ModalAnnulationRecu';
import { ModalRecuTable } from './ModalRecuTable';
import ModalRejetPrelevementRecu from './ModalRejetPrelevementRecu';

interface ModalRecuProps {
  envoiRecuParMail?: boolean;
  readonly?: boolean;
  recu: RecuEncaissementType;
  onClose: () => void;
  onAnnulation?: (recuAnnule: RecuEncaissementType) => void;
}

const ModalRecu = (props: ModalRecuProps) => {
  const { envoiRecuParMail, readonly, recu, onClose, onAnnulation } = props;

  const {
    numero,
    dateCreation,
    lignes,
    factures,
    regisseur,
    dateAnnulation,
    regisseurAnnulation,
    motifAnnulation,
    commentaire,
    paiementEnLigne,
  } = recu || {};

  const [reglements, setReglements] = React.useState<DataReglementType[]>([]);
  const [reglementsLoading, setReglementsLoading] = React.useState<boolean>(true);
  const [selectedReglements, setSelectedReglements] = React.useState<(number | string)[]>([]);
  const [recusAnterieurs, setRecusAnterieurs] = React.useState<RecuEncaissementType[]>([]);
  const [recusAnterieursLoading, setRecusAnterieursLoading] = React.useState<boolean>(false);
  // Ouverture de la modal de confirmation d'annulation
  const [annulation, setAnnulation] = React.useState<boolean>(false);
  // Ouverture de la modal de rejet du prélèvement en ligne, et son dernier message d'échec
  const [rejet, setRejet] = React.useState<boolean>(false);
  const [erreurRejet, setErreurRejet] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const annulationPossible = useRegieStore((state) => state.regie?.annulationPossible);
  // Le rejet est réservé au régisseur et au suppléant (habilitation 1 côté back).
  const isAgent = useRegisseurStore((state) => state.regisseur?.mandat === REGISSEUR.TYPE_AGENT);

  const { onRecuCancel, onRecuReject } = React.useContext(RecuContext);

  const isPrelevementEnLigne =
    paiementEnLigne?.modeReglement === MODES_REGLEMENTS.PRELEVEMENT_INTERNET;

  const handleReglementSelect = React.useCallback((id: number | string) => {
    setSelectedReglements((prev) => {
      // Si celui sélectionné existe déjà, on le retire
      if (_.includes(prev, id)) {
        return _.without(prev, id);
      }

      // sinon on ajoute id au tableau
      return [...prev, id];
    });
  }, []);

  /**
   * Annule le reçu
   *
   * @param {string} motif
   */
  const handleAnnuleConfirm = React.useCallback(
    (motif) => {
      setLoading(true);

      onRecuCancel({ ...recu, motifAnnulation: motif })
        .then((recuAnnule) => {
          setAnnulation(false);
          onAnnulation?.(recuAnnule);
        })
        .catch((error) => {
          toast(extractApiErrorMessage(error), { type: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [onAnnulation, onRecuCancel, recu],
  );

  const handleAnnulationCancel = React.useCallback(() => {
    setAnnulation(false);
  }, []);

  /**
   * Rejette le prélèvement en ligne : déclaré à MFO, reçu annulé. En cas d'échec, la modale de
   * rejet reste ouverte avec le message du back.
   */
  const handleRejetConfirm = React.useCallback(
    (donnees: { motifRejet: string; dateLimitePaiement: string | null }) => {
      setLoading(true);
      setErreurRejet(null);

      onRecuReject(recu, donnees)
        .then((recuRejete) => {
          setRejet(false);
          onAnnulation?.(recuRejete);
        })
        .catch((error) => {
          setErreurRejet(extractApiErrorMessage(error));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [onAnnulation, onRecuReject, recu],
  );

  const handleRejetCancel = React.useCallback(() => {
    setRejet(false);
    setErreurRejet(null);
  }, []);

  // On récupère tous les règlements fait sur ce reçu
  const getReglements = React.useCallback(() => {
    setReglementsLoading(true);
    request
      .get(getRecuReglements(recu?.identifiant))
      .then((data: DataReglementType[]) => {
        setReglements(data);
      })
      .finally(() => {
        setReglementsLoading(false);
      });
  }, [recu?.identifiant]);

  // On récupère tous les recus antérieurs fait sur les factures de ce recu
  const getAnterieursRecu = React.useCallback(() => {
    setRecusAnterieursLoading(true);
    request
      .get(getRecusAnterieurs(recu?.identifiant))
      .then((data: RecuEncaissementType[]) => {
        setRecusAnterieurs(data);
      })
      .finally(() => {
        setRecusAnterieursLoading(false);
      });
  }, [recu?.identifiant]);

  const handlePrint = React.useCallback(
    (mail = false) => {
      setLoading(true);

      request
        .get(printRecu(recu?.identifiant, mail), {
          headers: { Accept: 'application/json, application/pdf' },
        })
        .then((response: any) => {
          // Si la réponse contient une URL temporaire (JSON)
          if (response.url) {
            window.open(response.url, '_blank');
          } else {
            // Sinon, traiter comme un blob PDF (réponse directe)
            const blob = response.data || response;
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            const url = URL.createObjectURL(pdfBlob);

            window.open(url, '_blank');

            // Nettoyer après un délai
            setTimeout(() => {
              URL.revokeObjectURL(url);
            }, 2000);
          }
        })
        .catch(() => {
          toast(
            mail
              ? "Erreur lors de l'envoi du reçu par mail"
              : "Une erreur est survenue lors de l'ouverture du reçu",
            { type: 'error' },
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [recu?.identifiant],
  );

  React.useEffect(() => {
    getReglements();
    getAnterieursRecu();
  }, [getReglements, getAnterieursRecu]);

  const isAnnule = React.useMemo(() => !_.isNil(regisseurAnnulation), [regisseurAnnulation]);

  // Un paiement carte en ligne est définitif : le back refuse aussi l'annulation (422), avec le
  // même message. Un prélèvement en ligne, lui, se rejette (bouton dédié).
  const raisonAnnulationImpossible =
    paiementEnLigne?.modeReglement === MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET
      ? 'Un paiement par carte bancaire en ligne est définitif : son reçu ne peut pas être annulé.'
      : null;

  // Reçu annulé par le rejet de son prélèvement en ligne
  const isRejete = isAnnule && paiementEnLigne?.etat === 'rejete';

  const renderContent = React.useMemo(
    () => (
      <Segment
        data-testid="content-modal-recu"
        basic
        style={{
          padding: 0,
        }}
      >
        {isAnnule && (
          <StripedMessage
            text={isRejete ? 'PRÉLÈVEMENT REJETÉ' : 'ANNULÉ'}
            rotationCoef={_.size(factures)}
          />
        )}
        {!_.isNil(motifAnnulation) && !_.isEmpty(motifAnnulation) && isAnnule && (
          <Segment basic style={{ padding: 0 }} as={Form}>
            <Form.Group style={{ margin: 0, gap: '1em' }}>
              <Header color="red" as="h5" style={{ margin: 0 }}>
                Motif d&apos;annulation :{' '}
              </Header>
              <Header color="red" as="h5" style={{ margin: 0 }}>
                {motifAnnulation}
              </Header>
            </Form.Group>
          </Segment>
        )}
        <ModalRecuTable
          reglements={reglements}
          selectedItems={selectedReglements}
          factures={factures}
          lignes={lignes}
          recusAnterieurs={recusAnterieurs}
          annule={isAnnule}
        />
        {reglements.length === 1 && (
          <>
            <br />
            <br />
          </>
        )}
        <Segment basic style={{ marginTop: 0, marginBottom: 0 }}>
          <ReglementsInline
            reglements={reglements}
            factures={factures}
            onSelect={handleReglementSelect}
            selectedItems={selectedReglements}
            datePaiementEnLigne={paiementEnLigne?.datePaiement}
          />
          <Divider style={{ margin: 0 }} />
        </Segment>
        <br />
        <Segment basic style={{ marginTop: 0 }}>
          {/* @ts-ignore */}
          <Form style={{ width: '100%' }}>
            <Form.Group inline style={{ width: '100%' }}>
              <Form.Field width={3}>
                <label>Commentaires internes : </label>
                <Icon name="shield" />
              </Form.Field>
              <TextArea
                readOnly
                rows={1}
                width={13}
                style={{ resize: 'none', minHeight: '50px' }}
                value={commentaire}
              />
            </Form.Group>
          </Form>
        </Segment>
      </Segment>
    ),
    [
      isAnnule,
      isRejete,
      motifAnnulation,
      reglements,
      selectedReglements,
      factures,
      lignes,
      recusAnterieurs,
      commentaire,
      handleReglementSelect,
      paiementEnLigne?.datePaiement,
    ],
  );

  const renderLoading = React.useMemo(
    () => (
      <List as={Form}>
        <List.Item style={{ height: 200 }}>
          <Dimmer active inverted>
            <Loader>Chargement des informations</Loader>
          </Dimmer>
        </List.Item>
      </List>
    ),
    [],
  );

  const boutonAnnulation = (
    <Button
      data-testid="button-annulation-recu"
      negative
      loading={loading}
      disabled={loading || annulationPossible === false || !_.isNil(raisonAnnulationImpossible)}
      onClick={() => setAnnulation(true)}
    >
      <Icon.Group>
        <Icon name="file alternate" />
        <Icon name="close" corner style={{ fontSize: '0.7em', top: 7, right: -1 }} />
      </Icon.Group>
      Annuler le reçu
    </Button>
  );

  const boutonRejet = (
    <Button
      data-testid="button-rejet-prelevement-recu"
      negative
      loading={loading}
      disabled={loading || annulationPossible === false || isAgent}
      onClick={() => setRejet(true)}
    >
      <Icon.Group>
        <Icon name="file alternate" />
        <Icon name="close" corner style={{ fontSize: '0.7em', top: 7, right: -1 }} />
      </Icon.Group>
      Rejeter le prélèvement
    </Button>
  );

  return (
    <Modal open closeIcon onClose={onClose} size="huge">
      <Modal.Header>
        Reçu N°
        {numero} du {dayjs(dateCreation).format('DD MMMM YYYY')}
        <span style={{ padding: '0px 20px' }}>-</span>
        {_.isNil(regisseur) ? (
          'Paiement en ligne'
        ) : (
          <>
            Établi par {regisseur.prenom} {regisseur.nom}
          </>
        )}
        {isAnnule && (
          <>
            <span style={{ padding: '0px 20px' }}>-</span>
            {isRejete ? 'Rejeté' : 'Annulé'} le {dayjs(dateAnnulation).format('DD MMMM YYYY')} par{' '}
            {regisseurAnnulation?.prenom} {regisseurAnnulation?.nom}
          </>
        )}
      </Modal.Header>
      <Modal.Content
        // @ts-expect-error
        style={{ overflow: 'hidden' }}
      >
        {reglementsLoading || recusAnterieursLoading ? renderLoading : renderContent}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} size="tiny" icon="close" content="Fermer" basic />
        {!readonly && !isAnnule && isPrelevementEnLigne && (
          <>
            {isAgent ? (
              // Le <span> porte le survol, qu'un bouton désactivé ne reçoit pas.
              <Popup
                position="top center"
                content="Réservé au régisseur et au suppléant."
                trigger={<span>{boutonRejet}</span>}
              />
            ) : (
              boutonRejet
            )}
            {rejet && (
              <ModalRejetPrelevementRecu
                identifiantRecu={recu.identifiant}
                dejaRejete={paiementEnLigne?.etat === 'rejete'}
                loading={loading}
                erreur={erreurRejet}
                onValidate={handleRejetConfirm}
                onClose={handleRejetCancel}
              />
            )}
          </>
        )}
        {!readonly && !isAnnule && !isPrelevementEnLigne && (
          <>
            {_.isNil(raisonAnnulationImpossible) ? (
              boutonAnnulation
            ) : (
              // Le <span> porte le survol, qu'un bouton désactivé ne reçoit pas.
              <Popup
                position="top center"
                content={raisonAnnulationImpossible}
                trigger={<span>{boutonAnnulation}</span>}
              />
            )}
            {annulation && (
              <ModalAnnulationRecu
                loading={loading}
                onValidate={handleAnnuleConfirm}
                onClose={handleAnnulationCancel}
              />
            )}
          </>
        )}
        {envoiRecuParMail && (
          <Button
            data-testid="button-send-mail-recu"
            positive
            disabled={loading}
            loading={loading}
            onClick={() => handlePrint(true)}
          >
            <Icon.Group>
              <Icon name="mail" />
              <Icon name="checkmark" corner style={{ fontSize: '0.7em', top: 7, right: -1 }} />
            </Icon.Group>
            Envoyer le reçu par mail
          </Button>
        )}
        <Button
          data-testid="button-download-recu"
          positive
          onClick={() => handlePrint()}
          loading={loading}
          disabled={loading}
        >
          <Icon.Group>
            <Icon name="download" />
            <Icon name="checkmark" corner style={{ fontSize: '0.7em', top: 7, right: -1 }} />
          </Icon.Group>
          Imprimer le reçu
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalRecu;
